import styled from "styled-components";
import { colors, tokens, focusRingColor } from "../../utils";
import { TextView } from "@components";

export const Container = styled.div`
  height: 8vh;
  width: 100%;
  background-color: ${colors.background};
  display: grid;
  /* ISP-0000 header-390: bare 40/20/40 % tracks default to auto sizing, so
     unwrapped children (username text, search bar) force the tracks past
     the container width at narrow viewports (measured 226px page x-scroll
     at 376px client width). minmax(0, ...) lets tracks actually shrink. */
  grid-template-columns: minmax(0, 40%) minmax(0, 20%) minmax(0, 40%);
  box-shadow: 0px 4px 7px 0px #00000014;
`;

// G3 (coverage doc): wraps the header's actual navigational controls in a
// real <nav> landmark. `display: contents` removes the element's own box
// from layout entirely, so it does NOT count as a 4th item against
// Container's `grid-template-columns: 40% 20% 40%` (which expects exactly
// 3 track children) — LeftContainer/CenterContainer/RightContainer keep
// participating in the grid as direct items, byte-identical layout.
export const NavRegion = styled.nav`
  display: contents;
`;

export const LeftContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;
`;

export const RightContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
`;

// GR-2: header chat-indicator (the DM ENTRY POINT, Facebook-style). Line-art
// icon + unread count badge + native "Messages" tooltip; opens the bottom-right
// dock. Purple brand accent (#9B5AF8). ≥44px hit target.
export const ChatIndicatorBtn = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-right: 8px;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 50%;
  color: ${colors.PrimaryPurple500};
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${colors.PrimaryPurple100};
  }
  &:focus-visible {
    outline: 2px solid ${focusRingColor};
    outline-offset: 2px;
  }
  svg {
    width: 22px;
    height: 22px;
  }
`;

export const ChatBadge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* a11y: same AA fix as the gallery-header badge — white 11px digits need the
     on-fill token (#8F38EF, 5.28:1), not PrimaryPurple500 (#9B5AF8, 4.03:1). */
  background-color: ${tokens.brand.primaryActive};
  color: ${colors.white};
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  border-radius: 9px;
  box-shadow: 0 0 0 2px ${colors.background};
  pointer-events: none;
`;

export const CenterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

export const Logo = styled.img`
  height: 6vh;
  width: 6vh;
  margin-left: 3vw;
  margin-right: 3vw;
  cursor: pointer;
`;

export const SearchBar = styled.div`
  text-align: left;
  min-width: 0;
  overflow: hidden;
`;

export const CreateWheelContainer = styled.div`
  position: absolute;
  top: 0;
  border: 10px solid ${colors.background};
  background-color: ${colors.background};
  border-radius: 50%;
  height: 10vh;
  width: 10vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  z-index: 100;
  box-shadow: 0px 13px 8px -5px #00000014;
  cursor: pointer;
`;

export const InviteContainer = styled.div`
  text-align: center;
  background-color: ${colors.primary};
  color:${colors.white};
  font-size: 14px;
  cursor: pointer;
  width: 140px;
  height: 5vh;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  margin-right: 1vw;
`;

export const FeedbackIcon = styled.img`
  height: 24px;
  width: 24px;
  margin-right: 5px;
`;
export const UserContainer = styled.div`
  text-align: right;
  display: flex;
  align-items: center;
  margin-right: 1vw;
  padding-right: 1vw;
  padding-left: 1vw;
  position: relative;
  border-radius: 5px;
  padding-top: 5px;
  padding-bottom: 5px;

  ${(props) =>
    props.clicked &&
    `
      background-color: ${colors.lightBlue};
      
    `}

  &:hover {
    background-color: ${colors.lightBlue};
    cursor: pointer;
  }
`;

export const CreateWheelImage = styled.img`
  background-color: ${colors.background};
  width: 100%;
  height: 100%;
`;

export const LoggingOut = styled(TextView)`
  display: inline-block;
  clip-path: inset(0 4ch 0 0);
  animation: l 1s steps(4) infinite;
  font-weight: 700;
`;

/* ISP-0000 header-390: the display name rendered full-width/no-wrap was the
   single biggest contributor to RightContainer's intrinsic content width.
   Truncate it, and drop it entirely under 480px (avatar + chevron stay). */
export const UserName = styled(TextView)`
  max-width: 110px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 480px) {
    display: none;
  }
`;

export const ButtonView = styled.div`
  display: flex;
  align-items: center;
  justify-content: right;
`;

export const Button = styled.div`
  width: 90px;
  height: 4vh;
  background-color: ${colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    background-color: ${colors.lightBlue};
  }
`;

export const Dropdown = styled.div`
  position: absolute;
  background-color: ${colors.white};
  min-width: 160px;
  top: 7vh;
  box-shadow: 0px 4px 7px 0px #00000014;
  border-radius: 5px;
  padding: 10px 0px;
  left: 0;
  z-index: 10;
`;

// Register bug #909: this item sat flush against Dropdown's left/right edges
// (Dropdown's own padding is vertical-only, 0 horizontal) with no border-radius
// of its own -- a square-cornered hover fill inside a 5px-rounded container,
// visibly overflowing the container's rounded corner on the first/last row.
// hoverHighlight gives every hover-highlighted row the app's ONE shared
// text↔edge padding + corner radius.
export const DropdownItem = styled.div`
  text-align: left;
  height: 34px;
  font-size: 14px;
  display: flex;
  align-items: center;
  padding: ${tokens.hoverHighlight.paddingY}px ${tokens.hoverHighlight.paddingX}px;
  border-radius: ${tokens.hoverHighlight.radius}px;

  &:hover {
    background-color: ${colors.lightBlue};
  }
`;

export const DropdownImage = styled.img`
  width: 14px;
  height: 14px;
  margin-right: 5px;
`;

export const Beta = styled.img`
  width: 70px;
  height: 32px;
  margin-right:10px;
  cursor:pointer;
`;